/**
 * The reasons an admin records a manual deposit or withdrawal against.
 *
 * The service still stores `reason` as a free string — nothing here is an
 * enum on the wire. The list exists because the same four reasons were being
 * retyped by hand every time, each with slightly different wording, which
 * made the audit trail impossible to group by. Picking from a list writes a
 * consistent sentence; "Other" hands the box back.
 *
 * Deposits and withdrawals share the list. "Forgot to upload proof of
 * payment" reads as deposit-shaped, but support hits it on withdrawals too,
 * and two near-identical lists would drift apart.
 */

/** Selected when the admin wants to write their own reason. */
export const CUSTOM_REASON = 'OTHER';

export const MANUAL_TX_REASONS = [
  {value: 'WRONG_CURRENCY', label: 'Wrong currency'},
  {value: 'WRONG_AMOUNT', label: 'Wrong amount'},
  {
    value: 'MISSING_PROOF_OF_PAYMENT',
    label: 'Forgot to upload proof of payment',
  },
  {value: CUSTOM_REASON, label: 'Other'},
];

/**
 * The languages a reason can be written in.
 *
 * Frontend-only: the selection is never sent. It says which language the text
 * in the box is currently in, so the Translate button knows what it is
 * translating from, and so the admin can aim it at the language the user
 * actually reads. Only the resulting string is submitted.
 *
 * Spelled out rather than shown as EN / FR.
 */
export const REASON_LANGUAGES = [
  {value: 'EN', label: 'English'},
  {value: 'FR', label: 'French'},
];

export const languageLabel = value =>
  REASON_LANGUAGES.find(language => language.value === value)?.label || value;

/**
 * The sentence a preset writes into the box.
 *
 * Kept separate from the option label: the label is a column heading in a
 * dropdown ("Wrong amount"), while the stored reason is read months later in
 * an audit with no dropdown around it, so it has to stand on its own.
 *
 * `CUSTOM_REASON` deliberately has no text — choosing it clears the box.
 */
const REASON_TEXT = {
  EN: {
    WRONG_CURRENCY: 'Deposit was made in the wrong currency.',
    WRONG_AMOUNT: 'Deposit was made for the wrong amount.',
    MISSING_PROOF_OF_PAYMENT:
      'The user did not upload a proof of payment for this transaction.',
  },
  // Pre-translated rather than fetched: these four are fixed, and making the
  // admin wait on the translation service for a sentence we already know is
  // both slower and one more thing that can fail mid-form. The Translate
  // button is for text they wrote themselves.
  FR: {
    WRONG_CURRENCY: 'Le dépôt a été effectué dans la mauvaise devise.',
    WRONG_AMOUNT: 'Le dépôt a été effectué pour un montant incorrect.',
    MISSING_PROOF_OF_PAYMENT:
      "L'utilisateur n'a pas téléversé de preuve de paiement pour cette transaction.",
  },
};

export const reasonText = (preset, language = 'EN') =>
  REASON_TEXT[language]?.[preset] ?? REASON_TEXT.EN[preset] ?? '';

/**
 * Which preset, if any, produced the text currently in the box.
 *
 * Used to decide whether retyping has turned a preset into a custom reason —
 * once the admin edits the sentence, the dropdown must stop claiming it is
 * still the canned one.
 */
export const presetForText = (text = '') => {
  const trimmed = String(text ?? '').trim();
  if (!trimmed) return undefined;

  return MANUAL_TX_REASONS.map(reason => reason.value).find(preset =>
    REASON_LANGUAGES.some(
      language => reasonText(preset, language.value) === trimmed,
    ),
  );
};

/**
 * What goes on the wire when the admin leaves the reason blank.
 *
 * The reason is optional to fill in, but not optional to send: `reason` is
 * `String!` on `CreateManualWalletTransactionInput`, and the service rejects
 * an empty string with a bare "Unexpected error." before it even looks the
 * user up. So a blank box submits this instead.
 *
 * Worded as a statement of fact rather than something neutral like "Manual
 * adjustment", because it lands in an audit trail: a reader months later must
 * be able to tell that nobody gave a reason, not be handed a plausible one
 * the admin never wrote.
 *
 * Delete this the day `reason` becomes nullable, or starts accepting "".
 */
export const NO_REASON_GIVEN = 'No reason provided';
